/**
 * PlayerSessionView - Participant's view of a live interactive session
 * Mobile-friendly page for participants to join and interact with manager-led sessions
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Send,
  Dice5,
  Edit2,
  Check,
  X,
  AlertCircle,
  Pause,
  Lock,
  Clock,
  UserX,
  Vote,
  CheckCircle2,
  BarChart3,
} from 'lucide-react';
import VersionBadge from './VersionBadge';
import { API_BASE_URL } from '../config/api';

const API_URL = API_BASE_URL;

const PlayerSessionView = () => {
  const { sessionId } = useParams();
  
  // Session state
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expired, setExpired] = useState(false);
  
  // Security state
  const [requiresPin, setRequiresPin] = useState(false);
  const [pinInput, setPinInput] = useState('');
  const [pinError, setPinError] = useState(null);
  const [visitorStatus, setVisitorStatus] = useState('approved'); // pending, approved, kicked
  
  // Debug state
  const [showDebug, setShowDebug] = useState(false);
  
  // Visitor state
  const [visitorToken, setVisitorToken] = useState(null);
  const [visitorName, setVisitorName] = useState('');
  const [isEditingName, setIsEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  
  // Chat state
  const [messages, setMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const chatEndRef = useRef(null);
  
  // Vote state (legacy)
  const [voteValue, setVoteValue] = useState('');
  const [hasVoted, setHasVoted] = useState(false);
  
  // Enhanced Vote state
  const [activeVote, setActiveVote] = useState(null);
  const [voteCountdown, setVoteCountdown] = useState(0);
  const [myVote, setMyVote] = useState(null);
  const [isVoting, setIsVoting] = useState(false);
  const [sharedResults, setSharedResults] = useState(null);
  const [freeTextInput, setFreeTextInput] = useState('');
  
  // Dice state
  const [pendingRoll, setPendingRoll] = useState(null);
  const [isRolling, setIsRolling] = useState(false);

  // Check if session requires PIN on mount
  useEffect(() => {
    const checkSessionRequirements = async () => {
      try {
        // First check if we have an existing token
        const storedToken = localStorage.getItem(`session_${sessionId}_token`);
        
        if (storedToken) {
          // Try to rejoin with existing token
          await joinWithToken(storedToken);
        } else {
          // Check if PIN is required
          const response = await fetch(`${API_URL}/api/live-session/${sessionId}/requires-pin`);
          
          if (response.status === 404) {
            setError('Session not found');
            setLoading(false);
            return;
          }
          
          if (!response.ok) throw new Error('Failed to check session');
          
          const data = await response.json();
          
          if (data.requires_pin) {
            setRequiresPin(true);
            setLoading(false);
          } else {
            // No PIN required, join directly
            await joinSession(null);
          }
        }
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    };
    
    checkSessionRequirements();
  }, [sessionId]);

  // Join with existing token
  const joinWithToken = async (token) => {
    try {
      const storedName = localStorage.getItem(`session_${sessionId}_name`);
      
      const response = await fetch(`${API_URL}/api/live-session/${sessionId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ existing_token: token })
      });
      
      if (response.status === 404) {
        setError('Session not found');
        setLoading(false);
        return;
      }
      
      if (response.status === 410) {
        setExpired(true);
        setLoading(false);
        return;
      }
      
      if (response.status === 403) {
        // Token was kicked, clear and require re-join
        localStorage.removeItem(`session_${sessionId}_token`);
        localStorage.removeItem(`session_${sessionId}_name`);
        setError('You have been removed from this session');
        setLoading(false);
        return;
      }
      
      if (!response.ok) throw new Error('Failed to join session');
      
      const data = await response.json();
      setVisitorToken(data.visitor_token);
      setVisitorName(storedName || data.visitor_name);
      setVisitorStatus(data.status);
      setSession(data.session_info);
      
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Join session (with optional PIN)
  const joinSession = async (pin) => {
    try {
      setLoading(true);
      setPinError(null);
      
      const response = await fetch(`${API_URL}/api/live-session/${sessionId}/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: pin })
      });
      
      if (response.status === 404) {
        setError('Session not found');
        setLoading(false);
        return;
      }
      
      if (response.status === 410) {
        setExpired(true);
        setLoading(false);
        return;
      }
      
      if (response.status === 401) {
        const errData = await response.json();
        setPinError(errData.detail || 'Invalid PIN');
        setLoading(false);
        return;
      }
      
      if (!response.ok) throw new Error('Failed to join session');
      
      const data = await response.json();
      setVisitorToken(data.visitor_token);
      setVisitorName(data.visitor_name);
      setVisitorStatus(data.status);
      setSession(data.session_info);
      setRequiresPin(false);
      
      // Store token
      localStorage.setItem(`session_${sessionId}_token`, data.visitor_token);
      localStorage.setItem(`session_${sessionId}_name`, data.visitor_name);
      
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  // Handle PIN submit
  const handlePinSubmit = (e) => {
    e.preventDefault();
    if (pinInput.length === 4) {
      joinSession(pinInput);
    }
  };

  // Fetch session updates
  const fetchSession = useCallback(async () => {
    if (!sessionId) return;
    try {
      const response = await fetch(`${API_URL}/api/live-session/${sessionId}`);
      if (response.status === 410) {
        setExpired(true);
        return;
      }
      if (response.ok) {
        const data = await response.json();
        setSession(data);
      }
    } catch (err) {
      console.error('Error fetching session:', err);
    }
  }, [sessionId]);

  // Fetch chat messages
  const fetchMessages = useCallback(async () => {
    if (!sessionId) return;
    try {
      const response = await fetch(`${API_URL}/api/live-session/${sessionId}/chat?limit=100`);
      if (response.ok) {
        const data = await response.json();
        setMessages(data);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  }, [sessionId]);

  // Fetch vote status (legacy - for backward compatibility)
  const fetchVote = useCallback(async () => {
    if (!sessionId) return;
    try {
      const response = await fetch(`${API_URL}/api/live-session/${sessionId}/vote`);
      if (response.ok) {
        const data = await response.json();
        // Check if user has voted
        if (data.results) {
          const foundVote = data.results.find(r => r.voters.includes(visitorName));
          setHasVoted(!!foundVote);
          if (foundVote) setVoteValue(foundVote.value);
        }
      }
    } catch (err) {
      console.error('Error fetching vote:', err);
    }
  }, [sessionId, visitorName]);

  // Fetch vote results (when shared by manager)
  const fetchVoteResults = useCallback(async () => {
    if (!sessionId) return;
    try {
      const response = await fetch(
        `${API_URL}/api/live-session/${sessionId}/vote/results?show_names=true`
      );
      if (response.ok) {
        const data = await response.json();
        setSharedResults(data);
      }
    } catch (err) {
      console.error('Error fetching vote results:', err);
    }
  }, [sessionId]);

  // Fetch enhanced vote state
  const fetchVoteState = useCallback(async () => {
    if (!sessionId || !visitorToken) return;
    try {
      const response = await fetch(
        `${API_URL}/api/live-session/${sessionId}/vote/state?visitor_token=${visitorToken}`
      );
      if (response.ok) {
        const data = await response.json();
        
        if (data.is_active) {
          setActiveVote(data);
          
          // Calculate countdown
          if (data.ends_at) {
            const endsAt = new Date(data.ends_at);
            const now = new Date();
            const remaining = Math.max(0, Math.floor((endsAt - now) / 1000));
            setVoteCountdown(remaining);
          }
          
          // Set my vote if I've already voted
          if (data.my_vote) {
            setMyVote(data.my_vote);
          }
          
          // Check if results are shared
          if (data.results_shared) {
            fetchVoteResults();
          }
        } else if (data.vote_id && data.results_shared) {
          // Vote ended but results are shared
          setActiveVote(data);
          fetchVoteResults();
        } else {
          // No active vote
          setActiveVote(null);
          setVoteCountdown(0);
          // Only clear myVote if there's no active vote at all
          if (!data.vote_id) {
            setMyVote(null);
            setSharedResults(null);
            setFreeTextInput('');
          }
        }
      }
    } catch (err) {
      console.error('Error fetching vote state:', err);
    }
  }, [sessionId, visitorToken, fetchVoteResults]);

  // Cast vote (enhanced)
  const castVote = async (value) => {
    if (!sessionId || !visitorToken || isVoting) return;
    
    setIsVoting(true);
    try {
      const response = await fetch(`${API_URL}/api/live-session/${sessionId}/vote/cast`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitor_token: visitorToken,
          visitor_name: visitorName,
          vote_value: value
        })
      });
      
      if (response.ok) {
        setMyVote(value);
        setFreeTextInput('');
        // Refresh vote state
        fetchVoteState();
      } else {
        const errorData = await response.json();
        console.error('Vote failed:', errorData.detail);
      }
    } catch (err) {
      console.error('Error casting vote:', err);
    } finally {
      setIsVoting(false);
    }
  };

  // Check for pending dice roll
  const checkPendingRoll = useCallback(async () => {
    if (!sessionId || !visitorToken) return;
    try {
      const response = await fetch(
        `${API_URL}/api/live-session/${sessionId}/roll/pending?visitor_token=${visitorToken}`
      );
      if (response.ok) {
        const data = await response.json();
        if (data.pending && !data.already_rolled) {
          setPendingRoll(data);
        } else {
          setPendingRoll(null);
        }
      }
    } catch (err) {
      console.error('Error checking pending roll:', err);
    }
  }, [sessionId, visitorToken]);

  // Check visitor status (for pending approval)
  const checkMyStatus = useCallback(async () => {
    if (!sessionId || !visitorToken) return;
    try {
      const response = await fetch(
        `${API_URL}/api/live-session/${sessionId}/my-status?visitor_token=${visitorToken}`
      );
      if (response.ok) {
        const data = await response.json();
        setVisitorStatus(data.status);
        
        // If kicked, show error
        if (data.status === 'kicked') {
          setError('You have been removed from this session');
        }
      }
    } catch (err) {
      console.error('Error checking status:', err);
    }
  }, [sessionId, visitorToken]);

  // Heartbeat to keep visitor active
  const sendHeartbeat = useCallback(async () => {
    if (!sessionId || !visitorToken) return;
    try {
      await fetch(
        `${API_URL}/api/live-session/${sessionId}/heartbeat?visitor_token=${visitorToken}`,
        { method: 'POST' }
      );
    } catch (err) {
      console.error('Heartbeat error:', err);
    }
  }, [sessionId, visitorToken]);

  // Polling
  useEffect(() => {
    if (!session || expired || visitorStatus === 'kicked') return;
    
    const interval = setInterval(() => {
      fetchSession();
      fetchMessages();
      fetchVote();
      fetchVoteState();
      checkPendingRoll();
      sendHeartbeat();
      checkMyStatus();
    }, 3000);
    
    // Initial fetch
    fetchMessages();
    fetchVote();
    fetchVoteState();
    checkPendingRoll();
    
    return () => clearInterval(interval);
  }, [session, expired, visitorStatus, fetchSession, fetchMessages, fetchVote, fetchVoteState, checkPendingRoll, sendHeartbeat, checkMyStatus]);

  // Vote countdown timer
  useEffect(() => {
    if (!activeVote?.is_active || voteCountdown <= 0) return;
    
    const timer = setInterval(() => {
      setVoteCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          // Refresh vote state when countdown reaches 0
          fetchVoteState();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    return () => clearInterval(timer);
  }, [activeVote?.is_active, voteCountdown, fetchVoteState]);

  // Format countdown time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return mins > 0 ? `${mins}:${secs.toString().padStart(2, '0')}` : `${secs}s`;
  };

  // Auto-scroll chat
  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Rename visitor
  const handleRename = async () => {
    if (!newName.trim()) return;
    try {
      await fetch(`${API_URL}/api/live-session/${sessionId}/visitor`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitor_token: visitorToken,
          new_name: newName.trim()
        })
      });
      setVisitorName(newName.trim());
      localStorage.setItem(`session_${sessionId}_name`, newName.trim());
      setIsEditingName(false);
    } catch (err) {
      console.error('Error renaming:', err);
    }
  };

  // Send chat message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || session?.is_paused) return;
    
    try {
      await fetch(`${API_URL}/api/live-session/${sessionId}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitor_token: visitorToken,
          visitor_name: visitorName,
          text: chatInput.trim()
        })
      });
      setChatInput('');
      fetchMessages();
    } catch (err) {
      console.error('Error sending message:', err);
    }
  };

  // Submit vote
  const handleVote = async () => {
    if (!voteValue.trim() || session?.is_paused) return;
    try {
      await fetch(`${API_URL}/api/live-session/${sessionId}/vote`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitor_token: visitorToken,
          visitor_name: visitorName,
          value: voteValue.trim()
        })
      });
      setHasVoted(true);
      fetchVote();
    } catch (err) {
      console.error('Error voting:', err);
    }
  };

  // Roll dice
  const handleRoll = async () => {
    if (!pendingRoll || isRolling) return;
    setIsRolling(true);
    
    try {
      // Parse dice expression and generate random rolls
      const expression = pendingRoll.dice_expression;
      const values = [];
      let sum = 0;
      
      // Simple parsing for expressions like "2d6 + 1d4"
      const parts = expression.replace(/\s/g, '').split('+');
      for (const part of parts) {
        const match = part.match(/(\d*)d(\d+)/i);
        if (match) {
          const count = parseInt(match[1]) || 1;
          const sides = parseInt(match[2]);
          for (let i = 0; i < count; i++) {
            const roll = Math.floor(Math.random() * sides) + 1;
            values.push(roll);
            sum += roll;
          }
        }
      }
      
      await fetch(`${API_URL}/api/live-session/${sessionId}/roll/result`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          visitor_token: visitorToken,
          visitor_name: visitorName,
          roll_id: pendingRoll.roll_id,
          values,
          sum
        })
      });
      
      setPendingRoll(null);
      fetchMessages();
    } catch (err) {
      console.error('Error rolling:', err);
    } finally {
      setIsRolling(false);
    }
  };

  // Parse choices/links from content
  const parseChoices = (content) => {
    const choices = [];
    const linkRegex = /\[\[([^\]|]+)(?:\|([^\]]+))?\]\]/g;
    let match;
    while ((match = linkRegex.exec(content || '')) !== null) {
      const displayText = match[1];
      const target = match[2] || match[1];
      choices.push({ displayText, target });
    }
    return choices;
  };

  // Remove link markup from content for display
  const cleanContent = (content) => {
    if (!content) return '';
    return content
      .replace(/\[\[([^\]|]+)\|[^\]]+\]\]/g, '')
      .replace(/\[\[([^\]]+)\]\]/g, '')
      .trim();
  };

  // Convert markdown to HTML for display
  const formatContent = (content) => {
    if (!content) return '';
    let html = cleanContent(content);
    
    // Convert markdown tables to HTML
    html = convertMarkdownTables(html);
    
    // Bold: **text**
    html = html.replace(/\*\*(.+?)\*\*/g, '<strong class="text-amber-400">$1</strong>');
    
    // Italic: *text*
    html = html.replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em class="text-purple-400">$1</em>');
    
    // Strikethrough: ~~text~~
    html = html.replace(/~~(.+?)~~/g, '<del>$1</del>');
    
    // Line breaks
    html = html.replace(/\n\n/g, '<br/><br/>');
    html = html.replace(/\n/g, '<br/>');
    
    return html;
  };

  // Convert markdown tables to HTML
  const convertMarkdownTables = (text) => {
    const lines = text.split('\n');
    const result = [];
    let i = 0;
    
    while (i < lines.length) {
      const line = lines[i].trim();
      
      // Check if this looks like a table row
      if (line.includes('|') && !line.startsWith('[[')) {
        const tableLines = [];
        while (i < lines.length && lines[i].includes('|') && !lines[i].trim().startsWith('[[')) {
          tableLines.push(lines[i].trim());
          i++;
        }
        
        if (tableLines.length >= 2) {
          result.push(parseMarkdownTable(tableLines));
        } else {
          result.push(...tableLines);
        }
      } else {
        result.push(lines[i]);
        i++;
      }
    }
    
    return result.join('\n');
  };

  // Parse markdown table to HTML
  const parseMarkdownTable = (tableLines) => {
    const cleanedLines = tableLines.map(line => {
      let l = line.trim();
      if (l.startsWith('|')) l = l.slice(1);
      if (l.endsWith('|')) l = l.slice(0, -1);
      return l;
    });
    
    const separatorPattern = /^[\s\-:|]+$/;
    const hasHeader = cleanedLines.length > 1 && separatorPattern.test(cleanedLines[1]);
    
    let html = '<table class="w-full border-collapse my-4 text-sm bg-slate-800/50 rounded-lg overflow-hidden">';
    
    cleanedLines.forEach((line, idx) => {
      if (idx === 1 && hasHeader) return; // Skip separator
      
      const cells = line.split('|').map(c => c.trim());
      const isHeader = idx === 0 && hasHeader;
      const tag = isHeader ? 'th' : 'td';
      const cellClass = isHeader 
        ? 'bg-indigo-600 text-white font-semibold px-4 py-2 text-left' 
        : 'border-b border-slate-700 px-4 py-2';
      
      html += '<tr>';
      cells.forEach(cell => {
        let cellContent = cell
          .replace(/\*\*(.+?)\*\*/g, '<strong class="text-amber-400">$1</strong>')
          .replace(/(?<!\*)\*(?!\*)(.+?)(?<!\*)\*(?!\*)/g, '<em>$1</em>');
        html += `<${tag} class="${cellClass}">${cellContent}</${tag}>`;
      });
      html += '</tr>';
    });
    
    html += '</table>';
    return html;
  };

  // Get visible nodes based on history visibility
  const getVisibleContent = () => {
    if (!session) return [];
    
    const history = session.node_history || [];
    const visibility = session.history_visibility;
    
    if (visibility === 'NONE') {
      return [{
        title: session.current_node_title,
        content: session.current_node_content
      }];
    } else if (visibility === 'LAST_ONLY') {
      const nodes = [];
      if (history.length > 1) {
        // We don't have the content for previous nodes in this simple implementation
        // In production, you'd fetch this from the backend
      }
      nodes.push({
        title: session.current_node_title,
        content: session.current_node_content
      });
      return nodes;
    } else {
      // ALL - show current only for now (would need backend support for full history)
      return [{
        title: session.current_node_title,
        content: session.current_node_content
      }];
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-white text-lg">Loading session...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-red-900/50 border border-red-500 rounded-lg p-6 text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Error</h2>
          <p className="text-red-200">{error}</p>
        </div>
      </div>
    );
  }

  if (expired) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-slate-600 rounded-lg p-6 text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Session Expired</h2>
          <p className="text-slate-300">This session has ended or expired.</p>
        </div>
      </div>
    );
  }

  // PIN Input Screen
  if (requiresPin && !session) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-slate-600 rounded-lg p-6 text-center max-w-sm w-full">
          <Lock className="h-12 w-12 text-indigo-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Session Protected</h2>
          <p className="text-slate-300 mb-4">Enter the 4-digit PIN to join this session.</p>
          
          <form onSubmit={handlePinSubmit} className="space-y-4">
            <Input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={4}
              value={pinInput}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                setPinInput(value);
              }}
              placeholder="Enter PIN"
              className="text-center text-2xl tracking-widest font-mono bg-slate-700 border-slate-600 h-14"
              autoFocus
            />
            
            {pinError && (
              <p className="text-red-400 text-sm">{pinError}</p>
            )}
            
            <Button 
              type="submit"
              disabled={pinInput.length !== 4 || loading}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
            >
              {loading ? 'Joining...' : 'Join Session'}
            </Button>
          </form>
          
          <p className="text-xs text-slate-500 mt-4">
            Ask the Manager for the PIN
          </p>
        </div>
      </div>
    );
  }

  // Pending Approval Screen
  if (visitorStatus === 'pending') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-slate-800 border border-slate-600 rounded-lg p-6 text-center max-w-sm w-full">
          <Clock className="h-12 w-12 text-amber-400 mx-auto mb-4 animate-pulse" />
          <h2 className="text-xl font-bold text-white mb-2">Waiting for Approval</h2>
          <p className="text-slate-300 mb-4">
            The Manager needs to approve your request to join.
          </p>
          <p className="text-slate-400 text-sm">
            You are: <span className="text-amber-400 font-medium">{visitorName}</span>
          </p>
          <div className="mt-4 flex items-center justify-center gap-2 text-slate-500">
            <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
            <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
            <div className="w-2 h-2 bg-amber-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        </div>
      </div>
    );
  }

  // Kicked Screen
  if (visitorStatus === 'kicked') {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
        <div className="bg-red-900/50 border border-red-500 rounded-lg p-6 text-center max-w-md">
          <UserX className="h-12 w-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Removed from Session</h2>
          <p className="text-red-200">You have been removed from this session by the Manager.</p>
        </div>
      </div>
    );
  }

  const isPaused = session?.is_paused;
  const visibleContent = getVisibleContent();

  return (
    <div className="min-h-screen bg-slate-900 text-white flex flex-col">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 p-4">
        <div className="flex items-center justify-between max-w-2xl mx-auto">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-lg font-bold">{session?.story_title || 'Live Session'}</h1>
              <VersionBadge page="playerView" className="text-slate-500" />
            </div>
            <p className="text-xs text-slate-400">Session: {sessionId}</p>
          </div>
          <div className="flex items-center gap-2">
            {isEditingName ? (
              <>
                <Input
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-32 h-8 text-sm bg-slate-700 border-slate-600"
                  placeholder="Your name"
                />
                <Button size="sm" variant="ghost" onClick={handleRename}>
                  <Check className="h-4 w-4 text-green-400" />
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setIsEditingName(false)}>
                  <X className="h-4 w-4 text-red-400" />
                </Button>
              </>
            ) : (
              <>
                <span className="text-sm">{visitorName}</span>
                <Button 
                  size="sm" 
                  variant="ghost" 
                  onClick={() => {
                    setNewName(visitorName);
                    setIsEditingName(true);
                  }}
                >
                  <Edit2 className="h-4 w-4" />
                </Button>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Debug Toggle (small button in corner) */}
      <button
        onClick={() => setShowDebug(!showDebug)}
        className="fixed bottom-4 right-4 z-50 bg-slate-700 hover:bg-slate-600 text-xs px-2 py-1 rounded"
      >
        {showDebug ? 'Hide Debug' : '🐛'}
      </button>
      
      {/* Debug Panel */}
      {showDebug && (
        <div className="bg-slate-800 border-b border-slate-600 p-3 text-xs font-mono">
          <p className="text-amber-400 font-bold mb-1">Debug Info:</p>
          <p>Session ID: {sessionId}</p>
          <p>Story: {session?.story_title}</p>
          <p>Current Node ID: {session?.current_node_id}</p>
          <p>Current Node Title: {session?.current_node_title}</p>
          <p>Content Length: {session?.current_node_content?.length || 0} chars</p>
          <p>Is Paused: {session?.is_paused ? 'Yes' : 'No'}</p>
          <p>History Visibility: {session?.history_visibility}</p>
          <p>Visitor Token: {visitorToken?.substring(0, 10)}...</p>
        </div>
      )}

      {/* Paused Overlay */}
      {isPaused && (
        <div className="bg-amber-900/90 border-b border-amber-600 p-4 text-center">
          <div className="flex items-center justify-center gap-2">
            <Pause className="h-5 w-5" />
            <span className="font-medium">Manager has paused the session</span>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto p-4 max-w-2xl mx-auto w-full">
        {/* Story Content */}
        <div className="bg-slate-800 rounded-lg p-4 mb-4">
          {visibleContent.map((node, i) => {
            const choices = parseChoices(node.content);
            const formattedContent = formatContent(node.content);
            
            return (
              <div key={i} className="mb-4 last:mb-0">
                <h2 className="text-lg font-bold text-amber-400 mb-2">
                  {node.title || 'Current Scene'}
                </h2>
                <div className="prose prose-invert prose-sm mb-4">
                  {formattedContent ? (
                    <div 
                      className="text-slate-300"
                      dangerouslySetInnerHTML={{ __html: formattedContent }}
                    />
                  ) : (
                    <span className="text-slate-500 italic">No content</span>
                  )}
                </div>
                
                {/* Display choices */}
                {choices.length > 0 && (
                  <div className="border-t border-slate-700 pt-3">
                    <p className="text-xs text-slate-500 mb-2 uppercase tracking-wider">Choices:</p>
                    <div className="space-y-2">
                      {choices.map((choice, j) => (
                        <div 
                          key={j}
                          className="bg-slate-700/50 border border-slate-600 rounded-lg px-4 py-3 text-slate-200"
                        >
                          <span className="text-amber-400 mr-2">→</span>
                          {choice.displayText}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Pending Dice Roll */}
        {pendingRoll && (
          <div className="bg-amber-900/50 border border-amber-600 rounded-lg p-4 mb-4">
            <div className="flex items-center gap-2 mb-2">
              <Dice5 className="h-5 w-5 text-amber-400" />
              <span className="font-medium">Roll Request</span>
            </div>
            <p className="text-sm text-amber-200 mb-2">
              {pendingRoll.description || `Roll ${pendingRoll.dice_expression}`}
            </p>
            <Button 
              onClick={handleRoll}
              disabled={isRolling || isPaused}
              className="bg-amber-600 hover:bg-amber-700"
            >
              {isRolling ? 'Rolling...' : `🎲 Roll ${pendingRoll.dice_expression}`}
            </Button>
          </div>
        )}

        {/* Enhanced Voting Section */}
        {activeVote && (
          <div className="bg-gradient-to-br from-blue-900/70 to-indigo-900/70 border border-blue-500 rounded-lg p-4 mb-4 shadow-lg">
            {/* Vote Header */}
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Vote className="h-5 w-5 text-blue-400" />
                <span className="font-semibold text-blue-200">
                  {activeVote.vote_type === 'choice' ? 'Choice Vote' : 'Poll'}
                </span>
              </div>
              
              {/* Countdown Timer */}
              {activeVote.is_active && voteCountdown > 0 && (
                <div className="flex items-center gap-2 bg-red-600/80 px-3 py-1 rounded-full animate-pulse">
                  <Clock className="h-4 w-4 text-white" />
                  <span className="font-mono font-bold text-white">{formatTime(voteCountdown)}</span>
                </div>
              )}
              
              {/* Vote Ended Indicator */}
              {!activeVote.is_active && (
                <span className="px-3 py-1 bg-slate-600/80 text-slate-300 rounded-full text-sm">
                  Voting Ended
                </span>
              )}
            </div>
            
            {/* Question */}
            {activeVote.question && (
              <p className="text-white text-lg mb-3 font-medium">{activeVote.question}</p>
            )}
            {!activeVote.question && activeVote.vote_type === 'choice' && (
              <p className="text-blue-200 mb-3">Vote for your preferred choice:</p>
            )}
            
            {/* Voting Options */}
            {activeVote.is_active && (
              <div className="space-y-2 mb-3">
                {/* Choice/Poll with Options */}
                {activeVote.choices && activeVote.choices.length > 0 ? (
                  <div className="grid gap-2">
                    {activeVote.choices.map((choice, idx) => (
                      <Button
                        key={idx}
                        onClick={() => castVote(choice)}
                        disabled={isPaused || isVoting}
                        className={`w-full text-left justify-start py-3 transition-all ${
                          myVote === choice
                            ? 'bg-green-600 hover:bg-green-700 border-2 border-green-400'
                            : 'bg-slate-700 hover:bg-slate-600 border border-slate-500'
                        }`}
                      >
                        <div className="flex items-center gap-3 w-full">
                          {myVote === choice ? (
                            <CheckCircle2 className="h-5 w-5 text-green-300 flex-shrink-0" />
                          ) : (
                            <div className="w-5 h-5 border-2 border-slate-400 rounded-full flex-shrink-0" />
                          )}
                          <span className="flex-1 text-white">{choice}</span>
                          {myVote === choice && (
                            <span className="text-xs text-green-300 font-medium">Your vote</span>
                          )}
                        </div>
                      </Button>
                    ))}
                  </div>
                ) : (
                  /* Free Text Input (Poll without options) */
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <Input
                        value={freeTextInput}
                        onChange={(e) => setFreeTextInput(e.target.value)}
                        placeholder="Type your answer..."
                        disabled={isPaused || isVoting}
                        className="flex-1 bg-slate-700 border-slate-500 text-white placeholder:text-slate-400"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && freeTextInput.trim()) {
                            e.preventDefault();
                            castVote(freeTextInput.trim());
                          }
                        }}
                      />
                      <Button
                        onClick={() => castVote(freeTextInput.trim())}
                        disabled={isPaused || isVoting || !freeTextInput.trim()}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        {isVoting ? 'Voting...' : 'Submit'}
                      </Button>
                    </div>
                    {myVote && (
                      <div className="flex items-center gap-2 text-green-400 text-sm">
                        <CheckCircle2 className="h-4 w-4" />
                        <span>Your vote: <strong>{myVote}</strong></span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* My Vote Confirmation (when options exist) */}
            {myVote && activeVote.choices && activeVote.is_active && (
              <p className="text-green-400 text-sm flex items-center gap-2 mt-2">
                <CheckCircle2 className="h-4 w-4" />
                You can change your vote until voting ends
              </p>
            )}
            
            {/* Shared Results */}
            {sharedResults && !activeVote.is_active && (
              <div className="mt-4 bg-slate-800/80 rounded-lg p-3">
                <div className="flex items-center gap-2 mb-3">
                  <BarChart3 className="h-4 w-4 text-blue-400" />
                  <span className="font-medium text-blue-300">Results</span>
                  <span className="text-slate-400 text-sm">({sharedResults.total_votes} votes)</span>
                </div>
                
                <div className="space-y-2">
                  {sharedResults.results?.slice(0, 5).map((result, idx) => (
                    <div key={idx} className="space-y-1">
                      <div className="flex items-center justify-between text-sm">
                        <span className={`font-medium ${
                          idx === 0 ? 'text-yellow-400' : 'text-slate-300'
                        }`}>
                          {idx === 0 && '🏆 '}
                          {result.value}
                        </span>
                        <span className="text-slate-400">
                          {result.count} ({result.percentage}%)
                        </span>
                      </div>
                      <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full transition-all duration-500 ${
                            idx === 0 ? 'bg-yellow-500' : 'bg-blue-500'
                          }`}
                          style={{ width: `${result.percentage}%` }}
                        />
                      </div>
                      {result.voters && result.voters.length > 0 && (
                        <p className="text-xs text-slate-500 truncate">
                          {result.voters.join(', ')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Show if my vote was in the results */}
                {myVote && (
                  <p className="mt-3 text-sm text-slate-400 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    You voted: <strong className="text-white">{myVote}</strong>
                  </p>
                )}
              </div>
            )}
            
            {/* Waiting for results message */}
            {!activeVote.is_active && !sharedResults && (
              <div className="text-center py-4 text-slate-400">
                <Clock className="h-6 w-6 mx-auto mb-2 animate-pulse" />
                <p>Waiting for manager to share results...</p>
              </div>
            )}
          </div>
        )}

        {/* Legacy Vote Section (backward compatibility) */}
        {session?.current_vote_question && !activeVote && (
          <div className="bg-blue-900/50 border border-blue-600 rounded-lg p-4 mb-4">
            <h3 className="font-medium text-blue-300 mb-1">Vote</h3>
            <p className="text-white mb-1">{session.current_vote_question}</p>
            {session.current_vote_help_text && (
              <p className="text-xs text-blue-300 mb-2">{session.current_vote_help_text}</p>
            )}
            <div className="flex gap-2">
              <Input
                value={voteValue}
                onChange={(e) => setVoteValue(e.target.value)}
                placeholder="Your answer"
                disabled={isPaused}
                className="flex-1 bg-slate-700 border-slate-600"
              />
              <Button 
                onClick={handleVote}
                disabled={isPaused || !voteValue.trim()}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {hasVoted ? 'Update' : 'Vote'}
              </Button>
            </div>
          </div>
        )}

        {/* Chat */}
        <div className="bg-slate-800 rounded-lg overflow-hidden">
          <div className="p-3 border-b border-slate-700">
            <span className="font-medium">Chat</span>
          </div>
          <div className="h-64 overflow-y-auto p-3 space-y-2">
            {messages.length === 0 ? (
              <p className="text-slate-500 text-sm text-center">No messages yet</p>
            ) : (
              messages.map((msg) => (
                <div key={msg.id} className="text-sm">
                  <span className="font-medium text-purple-400">{msg.visitor_name}: </span>
                  <span className="text-slate-300">{msg.text}</span>
                </div>
              ))
            )}
            <div ref={chatEndRef} />
          </div>
          <form onSubmit={handleSendMessage} className="p-3 border-t border-slate-700 flex gap-2">
            <Input
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
              placeholder={isPaused ? 'Session paused' : 'Type a message...'}
              disabled={isPaused}
              className="flex-1 bg-slate-700 border-slate-600"
            />
            <Button type="submit" disabled={isPaused || !chatInput.trim()}>
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </main>
    </div>
  );
};

export default PlayerSessionView;
